import React, { useEffect, useState } from "react";
import AppContainer from "../../Components/Accounting/AppContainer";
import DataTable from "../../Components/Accounting/DataTable";
import { ajaxIntegration } from "../../util/accounting/remote/ajaxAccounting";
import functions from "../../util/accounting/functions";
import toast, { Toaster } from "react-hot-toast";
import AppModal from "../../Components/Accounting/AppModal";

const SCOPE_OPTIONS = [
  { value: "students", label: "Students", hint: "Register/enroll students, list roster" },
  { value: "academics", label: "Academics", hint: "Sync class groups, streams, years, terms" },
  { value: "fees", label: "Fees", hint: "Statements, balances, fee catalogue, bill a class group" },
  { value: "payments", label: "Payments", hint: "Record a fee payment" },
];

const ENDPOINTS = [
  { method: "POST", path: "integration/students/upsert", scope: "students", body: "{students:[{admission_number, full_name, ...}]}" },
  { method: "POST", path: "integration/students/list", scope: "students", body: "{status?, class_id?, page?}" },
  { method: "POST", path: "integration/students/enroll", scope: "students", body: "{admission_number, stream_code, academic_year, term_name}" },
  { method: "POST", path: "integration/academics/class-groups/upsert", scope: "academics", body: "{groups:[{code, name, level?}]}" },
  { method: "POST", path: "integration/academics/class-groups/list", scope: "academics", body: "{active?}" },
  { method: "POST", path: "integration/academics/streams/upsert", scope: "academics", body: "{streams:[{code, name, group_code, stream_label?, capacity?}]}" },
  { method: "POST", path: "integration/academics/streams/list", scope: "academics", body: "{group_code?, active?}" },
  { method: "POST", path: "integration/academics/years/list", scope: "academics", body: "{}" },
  { method: "POST", path: "integration/academics/terms/list", scope: "academics", body: "{academic_year?}" },
  { method: "POST", path: "integration/fees/items/list", scope: "fees", body: "{category?}" },
  { method: "POST", path: "integration/fees/structures", scope: "fees", body: "{academic_year_id?, term_id?, class_id?}" },
  { method: "POST", path: "integration/fees/balance", scope: "fees", body: "{admission_number}" },
  { method: "POST", path: "integration/students/statement", scope: "fees", body: "{admission_number, academic_year_id?}" },
  { method: "POST", path: "integration/students/import-balances", scope: "fees", body: "{financial_year_id, balances:[{admission_number, opening_balance, ...}]}" },
  { method: "POST", path: "integration/fees/bill-group", scope: "fees", body: "{group_code, academic_year, term_name, lines:[{item_code, amount}]}" },
  { method: "POST", path: "integration/payments/record", scope: "payments", body: "{admission_number, amount, payment_date?, bank_account_code?, ...}" },
];

const ApiKeys = () => {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ label: "", scopes: ["students", "academics", "fees", "payments"] });
  const [newKey, setNewKey] = useState(null);
  const [scopeFilter, setScopeFilter] = useState(null);

  useEffect(() => { fetchKeys(); }, []);

  const fetchKeys = async () => {
    setLoading(true);
    const res = await ajaxIntegration.listKeys();
    if (res.status === "OK") setKeys(res.details || []);
    setLoading(false);
  };

  const toggleScope = (value) => {
    setForm((f) => ({
      ...f,
      scopes: f.scopes.includes(value) ? f.scopes.filter((s) => s !== value) : [...f.scopes, value],
    }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.label.trim()) { toast.error("Label is required"); return; }
    if (!form.scopes.length) { toast.error("Select at least one scope"); return; }
    setSaving(true);
    const res = await ajaxIntegration.createKey({ label: form.label.trim(), scopes: form.scopes.join(",") });
    if (res.status === "OK") {
      setNewKey(res.details);
      fetchKeys();
      setForm({ label: "", scopes: ["students", "academics", "fees", "payments"] });
    } else toast.error(res.message);
    setSaving(false);
  };

  const handleRevoke = async (id, label) => {
    if (!window.confirm(`Revoke "${label}"? Any system using this key will immediately lose access.`)) return;
    const res = await ajaxIntegration.revokeKey({ id });
    if (res.status === "OK") { toast.success("Key revoked"); fetchKeys(); }
    else toast.error(res.message);
  };

  const copyKey = (key) => {
    navigator.clipboard?.writeText(key);
    toast.success("Copied to clipboard");
  };

  const keyColumns = [
    { key: "label", label: "Label", render: (v) => <strong>{v}</strong> },
    { key: "api_key", label: "Key", render: (v) => <code className="tx-12">{v}</code> },
    { key: "scopes", label: "Scopes", render: (v) => (v || "").split(",").map((s) => <span key={s} className="badge bg-secondary me-1">{s}</span>) },
    { key: "is_active", label: "Status", render: (v) => <span className={`badge bg-${v ? "success" : "secondary"}`}>{v ? "Active" : "Revoked"}</span> },
    { key: "last_used_at", label: "Last Used", render: (v) => (v ? functions.formatDate(v) : "Never") },
    { key: "id", label: "Actions", render: (id, row) => row.is_active ? (
      <button className="btn btn-sm btn-outline-danger" onClick={() => handleRevoke(id, row.label)}><i className="fa-solid fa-ban"></i></button>
    ) : null },
  ];

  const visibleEndpoints = scopeFilter ? ENDPOINTS.filter((e) => e.scope === scopeFilter) : ENDPOINTS;

  return (
    <AppContainer
      title="API Keys"
      previousPageTitle="Accounting"
      description="Generate an API key to let a school's existing student/academics system sync directly with Accountify — students, class groups and streams, enrollment, termly billing, balances and payments."
      actions={[
        { label: "Generate Key", icon: "fa-solid fa-key", className: "btn btn-primary", onClick: () => setShowCreate(true) },
      ]}
    >
      <Toaster />

      <div className="card custom-card p-3 mb-3">
        <DataTable columns={keyColumns} data={keys} loading={loading} pageSize={20} emptyMessage="No API keys yet. Generate one to connect an external academics system." />
      </div>

      <div className="card custom-card p-4">
        <h6 className="fw-semibold mb-2"><i className="fa-solid fa-plug me-2"></i>Integration Reference</h6>
        <p className="tx-12 tx-muted">
          Hand these endpoints to whoever maintains the school's existing academics system. Every call is a{" "}
          <code>POST</code> with header <code>X-API-Key: &lt;your key&gt;</code> and a JSON body, under this
          server's base URL (the same one the Accountify app itself uses). Responses follow{" "}
          <code>{"{status, message, details}"}</code>.
        </p>
        <div className="d-flex gap-2 flex-wrap mb-3">
          <button className={`btn btn-sm ${!scopeFilter ? "btn-primary" : "btn-outline-secondary"}`} onClick={() => setScopeFilter(null)}>All</button>
          {SCOPE_OPTIONS.map((s) => (
            <button key={s.value} className={`btn btn-sm ${scopeFilter === s.value ? "btn-primary" : "btn-outline-secondary"}`} onClick={() => setScopeFilter(s.value)}>
              {s.label}
            </button>
          ))}
        </div>
        <div className="table-responsive">
          <table className="table table-sm table-bordered" style={{ fontSize: "0.82rem" }}>
            <thead className="bg-light"><tr><th>Endpoint</th><th>Scope</th><th>Body</th></tr></thead>
            <tbody>
              {visibleEndpoints.map((e) => (
                <tr key={e.path}>
                  <td><code>{e.method} {e.path}</code></td>
                  <td><span className="badge bg-secondary">{e.scope}</span></td>
                  <td className="tx-muted"><code>{e.body}</code></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* GENERATE KEY MODAL */}
      {showCreate && (
        <AppModal
          title="Generate API Key"
          icon="fa-solid fa-key"
          onClose={() => { setShowCreate(false); setNewKey(null); }}
          onSubmit={newKey ? undefined : handleCreate}
          footer={
            newKey ? (
              <button type="button" className="btn btn-primary" onClick={() => { setShowCreate(false); setNewKey(null); }}>Done</button>
            ) : (
              <>
                <button type="button" className="btn btn-light" onClick={() => setShowCreate(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? "Generating..." : "Generate Key"}</button>
              </>
            )
          }
        >
          {newKey ? (
            <>
              <div className="alert alert-warning tx-13">
                <i className="fa-solid fa-triangle-exclamation me-1"></i>
                Copy this key now — it will not be shown again. Give it to the academics-system vendor along with the scopes below.
              </div>
              <label className="form-label tx-12 fw-semibold">API Key</label>
              <div className="input-group mb-3">
                <input className="form-control" readOnly value={newKey.api_key} />
                <button className="btn btn-outline-primary" type="button" onClick={() => copyKey(newKey.api_key)}><i className="fa-solid fa-copy"></i></button>
              </div>
              <div className="tx-12 tx-muted">Scopes: {newKey.scopes}</div>
            </>
          ) : (
            <>
              <div className="mb-3">
                <label className="form-label tx-12 fw-semibold">Label *</label>
                <input className="form-control" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="e.g. SchoolMaster SIS" required />
              </div>
              <label className="form-label tx-12 fw-semibold">Scopes *</label>
              {SCOPE_OPTIONS.map((s) => (
                <div className="form-check" key={s.value}>
                  <input className="form-check-input" type="checkbox" id={`scope-${s.value}`} checked={form.scopes.includes(s.value)} onChange={() => toggleScope(s.value)} />
                  <label className="form-check-label tx-13" htmlFor={`scope-${s.value}`}>
                    {s.label} <span className="tx-muted tx-11">— {s.hint}</span>
                  </label>
                </div>
              ))}
            </>
          )}
        </AppModal>
      )}
    </AppContainer>
  );
};

export default ApiKeys;
